import type { BookingState } from '../types/funnel';

export interface StateDef {
  id: BookingState;
  label: string;
  pipelineStage: string;
  meaning: string;
}

export const bookingStates: StateDef[] = [
  { id: 'quote_draft', label: 'Quote draft', pipelineStage: '03 Move Details Pending', meaning: 'Intake in progress or estimate not yet accepted.' },
  { id: 'estimate_accepted', label: 'Estimate accepted', pipelineStage: '04 Estimate Presented', meaning: 'Customer accepted the exact quote hash/version. Any material edit makes it stale.' },
  { id: 'signatures_outstanding', label: 'Required signatures outstanding', pipelineStage: '05 Signature Pending', meaning: 'Applicable booking acceptance / deposit or third-party authorization not yet signed.' },
  { id: 'signatures_completed', label: 'Required signatures completed', pipelineStage: '05 Signature Pending', meaning: 'Set only by verified HighLevel completed-document events.' },
  { id: 'deposit_pending', label: 'Deposit pending', pipelineStage: '05 Signature Pending', meaning: 'Hosted Stripe Checkout created; no verified payment.' },
  { id: 'deposit_paid', label: 'Deposit paid', pipelineStage: '06 Deposit Received', meaning: 'Verified Stripe webhook for exactly $99 USD on this booking ID.' },
  { id: 'zelle_verification_pending', label: 'Zelle verification pending', pipelineStage: '06 Deposit Received (pending)', meaning: 'Customer reports sending; human verifies with bank. Screenshots are not proof.' },
  { id: 'dispatch_pending', label: 'Dispatch pending', pipelineStage: '09 Human Review Exception', meaning: 'Requested date/time awaiting real capacity confirmation.' },
  { id: 'dispatch_confirmed', label: 'Dispatch confirmed', pipelineStage: '07 Booking Confirmed', meaning: 'Acceptance + signatures + verified deposit + dispatch authorization all match.' },
  { id: 'cancelled_refunded', label: 'Cancelled / refunded', pipelineStage: '09 Human Review Exception', meaning: 'Cancellation (72-hour rule) or refund per approved contract.' },
];

export const paymentStatuses = ['none', 'checkout_created', 'processing', 'paid_verified', 'failed', 'expired', 'zelle_pending', 'zelle_verified', 'refunded', 'disputed'];
export const documentStatuses = ['none', 'sent', 'viewed', 'partially_signed', 'completed', 'voided'];

export const fieldMapping: { field: string; source: string }[] = [
  { field: 'IM Move Date Requested', source: 'moveDate / dateUnsure + arrival (8 AM / noon / 3 PM / other)' },
  { field: 'IM Origin Address ZIP', source: 'originZip (full address + unit stay in backend)' },
  { field: 'IM Destination Address ZIP', source: 'destZip' },
  { field: 'IM Home Type', source: 'homeType' },
  { field: 'IM Bedrooms', source: 'bedrooms (studio/1–4/5+/few items)' },
  { field: 'IM Origin Access', source: 'floor · method · flights · elevator · gate · COI' },
  { field: 'IM Destination Access', source: 'same, destination' },
  { field: 'IM Origin Carry Feet', source: 'carryBand + carryFeet' },
  { field: 'IM Destination Carry Feet', source: 'carryBand + carryFeet' },
  { field: 'IM Packing Preference', source: 'packing + product quantities' },
  { field: 'IM Inventory Summary', source: 'customer-confirmed items + unsure flag + disassembly' },
  { field: 'IM Crew Review', source: 'recommended vs chosen crew · review reasons' },
  { field: 'IM Meta Lead ID', source: 'meta_lead_id (never fabricated)' },
  { field: 'IM Automation Owner', source: 'HighLevel | SmartMoving (one owner)' },
  { field: 'IM Contact Consent Status', source: 'quote/sms/aiCall + text version + timestamp' },
  { field: 'IM Service Area Review', source: 'zone result + validation status' },
  { field: 'IM Booking Verification', source: 'estimate / documents / deposit / dispatch statuses' },
];

export const notificationEvents = [
  'Booking acceptance signed',
  'Deposit card authorization signed',
  'Full amount authorization signed',
  'Third-party cardholder authorization signed',
  'Bill of Lading — each stage + completed',
  'Applicable release signed',
  'Verified $99 deposit (Stripe webhook)',
  'Zelle reported — needs human bank verification',
  'Customer reply — pause follow-up, assign, alert',
  'Booking confirmed',
  'Integration failure',
];
